import ReactDiffViewer from 'react-diff-viewer';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';

import { type RawInputChainResponse } from './inputs-db.query';

interface DiffSectionProps {
  data: RawInputChainResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  finalText?: string;
}

const MetadataInfo = ({
  item,
  label,
  onCopy,
}: {
  item: {
    text_size: number;
    is_original: boolean;
  };
  label: string;
  onCopy: () => void;
}) => (
  <div className="flex items-center gap-2">
    <span>
      <span className="text-foreground-muted">{label}:</span>{' '}
      {item.text_size.toLocaleString()} chars
      {item.is_original && (
        <span className="text-green-500 ml-1">(Original)</span>
      )}
    </span>
    <Button
      onClick={onCopy}
      variant="ghost"
      size="xs"
      tooltip={`Copy ${label.toLowerCase()} text`}
    >
      <Icons.copy className="h-3 w-3" />
    </Button>
  </div>
);

export const DiffSection = ({
  data,
  isLoading,
  error,
  finalText,
}: DiffSectionProps) => {
  if (isLoading)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-foreground-muted">
          Loading diff data...
        </div>
      </div>
    );
  if (error)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-destructive">
          Failed to load diff data: {String(error)}
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-foreground-muted">
          No diff data available
        </div>
      </div>
    );

  if (data.raw_inputs_deleted) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-foreground-muted">
          Raw inputs have been deleted.
        </div>
      </div>
    );
  }

  const sortedChain = [...data.raw_input_chain].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  if (sortedChain.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-foreground-muted">
          No processing steps found. This input has only one version.
        </div>
      </div>
    );
  }

  const originalItem = sortedChain[0];

  // Why: when final condensed text exists, compare against the last raw_input BEFORE it
  // so size deltas reflect actual changes rather than the appended Input node.
  let cleanedItem = sortedChain[sortedChain.length - 1];
  if (typeof finalText === 'string') {
    for (let i = sortedChain.length - 1; i >= 0; i--) {
      if (sortedChain[i].text !== finalText) {
        cleanedItem = sortedChain[i];
        break;
      }
    }
  }

  const condensedText =
    typeof finalText === 'string' ? finalText : cleanedItem.text;
  const condensedSize = condensedText.length;

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast.success('Copied!', {
      description: `${label} text copied to clipboard`,
    });
  };

  const commonStyles = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    fontSize: '12px',
  } as const;

  return (
    <div className="flex flex-col flex-1 min-h-0 p-2 space-y-2">
      <div className="flex items-center justify-between px-3 py-2 bg-surface rounded border border-border flex-shrink-0" />

      <div className="px-3 py-2 bg-surface rounded border border-border flex-shrink-0">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <MetadataInfo
              item={{ text_size: cleanedItem.text.length, is_original: false }}
              label="After cleaning"
              onCopy={() => copyToClipboard(cleanedItem.text, 'After cleaning')}
            />
            <MetadataInfo
              item={{ text_size: condensedSize, is_original: false }}
              label="After Condensation v2"
              onCopy={() =>
                copyToClipboard(condensedText, 'After Condensation v2')
              }
            />
          </div>
          {cleanedItem.text.length !== condensedSize && (
            <div className="text-foreground-muted">
              {condensedSize - cleanedItem.text.length > 0 ? '+' : ''}
              {(condensedSize - cleanedItem.text.length).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      <div className="border rounded-md flex-1 min-h-0 relative overflow-hidden">
        <div className="absolute inset-0 overflow-auto">
          <ReactDiffViewer
            oldValue={originalItem.text}
            newValue={condensedText}
            splitView
            hideLineNumbers={false}
            disableWordDiff={false}
            leftTitle="Original"
            rightTitle="Condensation v2"
            styles={{
              diffContainer: {
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
              },
              contentText: { ...commonStyles, color: '#212529' },
              diffRemoved: commonStyles,
              diffAdded: commonStyles,
              line: { ...commonStyles, color: '#212529' },
            }}
          />
        </div>
      </div>
    </div>
  );
};
